<template>
  <div class="x-modal-container">
    <!-- Desktop Modal -->
    <v-dialog
      v-if="!isMobile"
      :model-value="modelValue"
      @update:model-value="$emit('update:modelValue', $event)"
      :max-width="maxWidth"
      persistent
      transition="fade-transition"
    >
      <v-card class="guide-modal-card" :class="contentClass">
        <div v-if="showDesktopClose" class="guide-modal__header">
          <button
            class="guide-modal__close"
            @click="closeModal"
            aria-label="Close modal"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18"
                stroke="#3B4648"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6 6L18 18"
                stroke="#3B4648"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
        <img v-if="image" :src="image" :class="imageClass" alt="" />
        <slot></slot>
      </v-card>
    </v-dialog>

    <!-- Mobile Bottom Sheet -->
    <teleport to="body">
      <transition name="guide-fade">
        <div
          v-if="isMobile && modelValue"
          class="guide-sheet-overlay"
          @click="closeModal"
        ></div>
      </transition>
      <transition name="guide-slide">
        <div
          v-if="isMobile && modelValue"
          class="guide-sheet"
          :class="contentClass"
        >
          <div class="guide-sheet__header">
            <button
              class="guide-sheet__close"
              @click="closeModal"
              aria-label="Close modal"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6 6L18 18"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
          <img v-if="image" :src="image" :class="imageClass" alt="" />
          <slot></slot>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, defineProps, defineEmits } from "vue";

defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  maxWidth: {
    type: [Number, String],
    default: 430,
  },
  showDesktopClose: {
    type: Boolean,
    default: false,
  },
  contentClass: {
    type: String,
    default: "",
  },
  image: {
    type: String,
    default: "",
  },
  imageClass: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue", "close"]);

const isMobile = ref(false);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkMobile);
});

const closeModal = () => {
  emit("update:modelValue", false);
  emit("close");
  document.body.style.overflow = "";
};
</script>

<style scoped>
.guide-modal-card {
  background: white;
  border-radius: 12px !important;
  padding: 24px;
  width: 100%;
  box-shadow: none;
  border: none;
}

.guide-modal__header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.guide-modal__close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
}

/* ============ MOBILE BOTTOM SHEET ============ */
.guide-sheet-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.guide-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #ffffff;
  border-radius: 28px 28px 0 0;
  box-shadow:
    0px 4px 8px 3px rgba(0, 0, 0, 0.15),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  padding: 0 16px 24px 16px;
  overflow-y: auto;
  max-height: 90vh;
}

.guide-sheet__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 0;
  border-bottom: 1px solid #d0d5dd;
  margin-bottom: 16px;
}

.guide-sheet__close {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
}

/* ============ TRANSITIONS ============ */
.guide-fade-enter-active,
.guide-fade-leave-active {
  transition: opacity 0.3s ease;
}

.guide-fade-enter-from,
.guide-fade-leave-to {
  opacity: 0;
}

.guide-slide-enter-active,
.guide-slide-leave-active {
  transition: transform 0.3s ease;
}

.guide-slide-enter-from,
.guide-slide-leave-to {
  transform: translateY(100%);
}

@media (max-width: 768px) {
  .guide-modal-card {
    padding: 20px;
  }
}
</style>
